import { GetCategoryVariableColorMapResponse } from '@/api/dashboards/getCategoryVariableColorMap';
import { TreeNodeLike } from '@/components/TreeSelect/useTreeSelect/types';
import {
    FormattedSpendSummaryData,
    GetSpendSummaryWithRoiResponse,
} from '@/dashboards/SpendSummary/types';
import { DashboardVariable } from '@/dashboards/types';
import { createVariableMap } from '@/dashboards/utils/createVariableMap';

type GroupedSpendSummaryDataProps = GetSpendSummaryWithRoiResponse &
    GetCategoryVariableColorMapResponse & {
        dashboardVariables: DashboardVariable[];
    };

export const groupSpendSummaryData = ({
    rows,
    variableColorMap,
    categoryColorMap,
    dashboardVariables,
}: GroupedSpendSummaryDataProps): FormattedSpendSummaryData => {
    const treeMap: Record<string, TreeNodeLike> = {};

    const categoryData: FormattedSpendSummaryData['categoryData'] = [];
    const variableData: FormattedSpendSummaryData['variableData'] = [];
    const dates: Set<string> = new Set();

    const usedCategoryVariableCombinations = new Set<string>();
    const variableMap = createVariableMap(dashboardVariables);

    const data = rows.reduce(
        (
            acc,
            {
                variable,
                category,
                periodName: dateString,
                spend,
                contribution,
                contributionProduct,
            },
        ) => {
            const date = dateString;
            dates.add(date);

            const variableConfig = variableMap[variable];
            const displayName = variableConfig?.displayLabel ?? variable;

            if (!acc.contributionDataByVariable.has(displayName)) {
                const color = variableColorMap[variable]!;
                variableData.push({
                    variable,
                    category,
                    color,
                    displayName,
                });
                acc.contributionDataByVariable.set(displayName, {
                    data: {},
                    color,
                });
            }

            if (!acc.contributionDataByCategory.has(category)) {
                const color = categoryColorMap[category]!;
                categoryData.push({
                    category,
                    color,
                });
                acc.contributionDataByCategory.set(category, {
                    data: {},
                    color,
                });
                acc.totalContributionByCategory.set(category, 0);
            }

            acc.contributionDataByVariable.get(displayName)!.data[date] =
                (acc.contributionDataByVariable.get(displayName)!.data[date] ||
                    0) + contribution || contribution;

            acc.contributionDataByCategory.get(category)!.data[date] =
                (acc.contributionDataByCategory.get(category)!.data[date] ||
                    0) + contribution || contribution;

            acc.totalContribution += contribution;
            acc.totalContributionByCategory.set(
                category,
                acc.totalContributionByCategory.get(category)! + contribution,
            );
            acc.totalContributionByVariable.set(
                displayName,
                (acc.totalContributionByVariable.get(displayName) || 0) +
                    contribution,
            );

            if (!acc.spendDataByVariable.has(displayName)) {
                const color = variableColorMap[variable]!;
                acc.spendDataByVariable.set(displayName, {
                    data: {},
                    color,
                });
            }

            if (!acc.spendDataByCategory.has(category)) {
                const color = categoryColorMap[category]!;
                acc.spendDataByCategory.set(category, {
                    data: {},
                    color,
                });
                acc.totalSpendByCategory.set(category, 0);
            }

            acc.spendDataByVariable.get(displayName)!.data[date] =
                (acc.spendDataByVariable.get(displayName)!.data[date] || 0) +
                    spend || spend;

            acc.spendDataByCategory.get(category)!.data[date] =
                (acc.spendDataByCategory.get(category)!.data[date] || 0) +
                    spend || spend;

            acc.totalSpend += spend;
            acc.totalSpendByCategory.set(
                category,
                (acc.totalSpendByCategory.get(category) || 0) + spend,
            );
            acc.totalSpendByVariable.set(
                displayName,
                (acc.totalSpendByVariable.get(displayName) || 0) + spend,
            );

            acc.totalContributionProduct += contributionProduct;
            acc.totalContributionProductByCategory.set(
                category,
                (acc.totalContributionProductByCategory.get(category) || 0) +
                    contributionProduct,
            );
            acc.totalContributionProductByVariable.set(
                displayName,
                (acc.totalContributionProductByVariable.get(displayName) || 0) +
                    contributionProduct,
            );

            acc.variableToCategory.set(displayName, category);

            if (
                !usedCategoryVariableCombinations.has(`${category}-${variable}`)
            ) {
                usedCategoryVariableCombinations.add(`${category}-${variable}`);
                if (!treeMap[category]) {
                    treeMap[category] = {
                        id: category,
                        label: category,
                        children: [],
                    };
                }

                const hasNode = treeMap[category]!.children!.some(
                    (node) => node.label === displayName,
                );

                if (!hasNode) {
                    treeMap[category]!.children!.push({
                        id: variable,
                        label: displayName,
                    });
                }
            }

            return acc;
        },
        {
            contributionDataByVariable: new Map<
                string,
                {
                    data: { [key: string]: number };
                    color: string;
                }
            >(),
            contributionDataByCategory: new Map<
                string,
                {
                    data: { [key: string]: number };
                    color: string;
                }
            >(),
            spendDataByVariable: new Map<
                string,
                {
                    data: { [key: string]: number };
                    color: string;
                }
            >(),
            spendDataByCategory: new Map<
                string,
                {
                    data: { [key: string]: number };
                    color: string;
                }
            >(),
            totalContributionProductByVariable: new Map<string, number>(),
            totalContributionProductByCategory: new Map<string, number>(),
            totalContribution: 0,
            totalSpend: 0,
            totalContributionProduct: 0,
            totalContributionByCategory: new Map<string, number>(),
            totalContributionByVariable: new Map<string, number>(),
            totalSpendByCategory: new Map<string, number>(),
            totalSpendByVariable: new Map<string, number>(),
            variableToCategory: new Map<string, string>(),
            variableData: [],
            categoryData: [],
            treeNodes: [] as TreeNodeLike[],
        },
    );

    return {
        ...data,
        variableData,
        categoryData,
        dates: Array.from(dates),
        treeNodes: Object.values(treeMap),
    };
};
